<template>
  <Slideover :visible="visible" size="max-w-2xl">
    <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
      <div class="flex items-start justify-between space-x-3">
        <h2 class="text-lg leading-7 font-medium">
          {{ title }}
        </h2>
        <div class="h-7 flex items-center">
          <button
            type="button"
            aria-label="Close panel"
            @click="$emit('toggle-slideover')"
            class="text-green-300 hover:text-white transition ease-in-out duration-150"
          >
            <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
          </button>
        </div>
      </div>
    </header>

    <!-- Slideover Body -->
    <div class="relative px-4 py-2">
      <div
        v-if="orderDetails.length && !this.featureFlags.phase3d_care_app"
        class="my-4 text-center text-lg leading-6 font-medium text-gray-900"
      >
        Please select any line items that need to have their Print Date cleared
      </div>
      <div
        v-else
        class="my-4 text-center text-lg leading-6 font-medium text-gray-900"
      >
        <p class="mb-6">No items Print Date need to be cleared.</p>
        <p class="mb-6">Click Save to release the order.</p>
      </div>

      <div class="space-y-2" v-if="orderDetails.length && !this.featureFlags.phase3d_care_app">
        <div class="flex flex-col">
          <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div
              class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8"
            >
              <div
                class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg"
              >
                <table class="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th><input type="checkbox" checked disabled /></th>
                      <th>SKU</th>
                      <th>Ord</th>
                      <th>Com</th>
                      <th>ESD</th>
                      <th>Print Date</th>
                    </tr>
                  </thead>
                  <tbody
                    class="bg-white divide-y divide-gray-200 align-text-top"
                  >
                    <tr v-for="(product, index) in orderDetails" :key="index">
                      <td
                        class="px-2 py-1 whitespace-no-wrap text-sm leading-5 font-medium text-gray-900"
                      >
                        <input
                          type="checkbox"
                          :value="product.ItemNumber"
                          v-model="clearPrintDates"
                        />
                      </td>
                      <td
                        class="px-2 py-1 whitespace-no-wrap text-sm leading-5 text-gray-500 font-bold"
                      >
                        {{ product.SKU }}
                        <div
                          v-for="(child, index) in product.children"
                          :key="index"
                          class="ml-2"
                        >
                          <span class="text-xs font-normal text-gray-500">
                            {{ child.SKU }}
                          </span>
                        </div>
                      </td>
                      <!-- <td
                        class="px-2 py-1 whitespace-no-wrap text-xs leading-5 text-gray-500 text-center"
                      >
                      <div>Ordered: {{ product.QuantityOrdered }}</div>
                      <div>Comitted: {{ product.QuantityShipped }}</div>
                      </td> -->

                      <td
                        class="px-2 py-1 whitespace-no-wrap text-xs leading-5 text-gray-500 text-center"
                      >
                        {{ product.QuantityOrdered }}
                      </td>
                      <td
                        class="px-2 py-1 whitespace-no-wrap text-xs leading-5 text-gray-500 text-center"
                      >
                        {{ product.QuantityShipped }}
                      </td>

                      <td
                        class="px-2 py-1 whitespace-no-wrap text-sm leading-5 text-gray-500"
                      >
                        {{ product.ExpectedShipDate | longMonth }}
                      </td>
                      <td
                        class="px-2 py-1 whitespace-no-wrap text-sm leading-5 text-gray-500"
                      >
                        {{ product.DateShipped | longMonth }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Errors -->
      <div class="pt-4 flex justify-end">
        <span v-if="error" class="text-red-500">Something went wrong!</span>
      </div>

      <!-- Buttons -->
      <div class="flex-shrink-0 px-4 py-4 space-x-4 flex justify-end">
        <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
          <Button
            class="btn btn-white"
            v-on:click-handler="$emit('toggle-slideover')"
          >
            Cancel
          </Button>
        </span>

        <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
          <Button
            v-on:click-handler="releaseHold()"
            :disabled="saving"
            class="flex items-center space-x-2"
          >
            <span>Save</span>
            <LoadingSpinner v-if="saving" class="text-white" />
          </Button>
        </span>
      </div>
    </div>
  </Slideover>
</template>

<script>
import Slideover from "@/components/layout/Slideover.vue";
import dateFormatter from "@/mixins/dateFormatter";
import Button from "@/components/elements/Button.vue";

export default {
  name: "OrderRelease",
  components: {
    Slideover,
    Button,
  },
  props: {
    title: {
      type: String,
      default: "Title",
    },
    visible: {
      type: Boolean,
      default: false,
    },
    featureFlags: {
      type: Object,
    },
  },
  data() {
    return {
      saving: false,
      reasons: [],
      reasonsLoaded: false,
      processing: false,
      error: false,
      clearPrintDates: [],
    };
  },
  mixins: [dateFormatter],
  computed: {
    order() {
      return this.$store.getters["orders/get"]("order");
    },
    orderDetails() {
      if (!this.order.details) {
        return [];
      }

      return this.order.details
        .filter((product) => {
          if (product.Adjustment != "0") {
            return false;
          }

          if (product.Type == "P") {
            return false;
          }

          let sku = product.SKU.toLowerCase();
          for (let item of ["warranty", "giftcert", "giftwrap", "routeins"]) {
            if (sku.includes(item)) {
              return false;
            }
          }

          if (!product.DateShipped) {
            return false;
          }

          return true;
        })
        .map((product) => {
          product.children = this.order.details.filter(
            (child) => child.Type == "P" && product.ItemNumber == child.Parent
          );
          return product;
        });
    },
  },
  created() {},
  methods: {
    releaseHold() {
      this.saving = true;

      this.$store
        .dispatch("orders/releaseOrder", {
          OrderNumber: this.order.SourceOrderNumber,
          data: { clearPrintDates: this.clearPrintDates },
        })
        .then(() => {
          this.$emit("update-row");
          this.$emit("toggle-slideover");
        })
        .catch((e) => {
          this.isError = true;
          this.errorMsg = e.message;
          this.errorResponse = e.response.data;
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
  filters: {
    longMonth: dateFormatter.format,
  },
};
</script>
<style scoped lang="scss">
th {
  @apply px-2 py-2 bg-gray-200 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider;
}
</style>
